import React, { useState, useRef, useEffect } from 'react';
import {
    TextField,
    Paper,
    Typography,
    Box,
    CircularProgress,
    Grid,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { API } from '../../api/api';
import { END_POINTS } from '../../api/endpoints';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../context/ToastContext';
import { formatText } from '../../utils/textFormator';
import usePermissions from '../../utils/permissions';
import { screenBasisPermission } from '../../utils/permission';

const findPatient = async (query) => {
    // api hit need to add to find the patient
    return new Promise((resolve) => {
        setTimeout(async () => {
            let baseUrl =
                process.env.NODE_ENV === 'development'
                    ? process.env.REACT_APP_DEVELOP
                    : process.env.REACT_APP_PROD;
            const patient = await API.get(`${baseUrl}${END_POINTS.searchPatient(query)}`)
            const data = patient.data
            // const filtered = data.filter((p) =>
            //     p.firstName.toLowerCase().includes(query.toLowerCase())
            // );
            resolve(data);
        }, 600);
    });
};

export default function SearchDropdown({handleBookAppointment}) {
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const debounceTimeout = useRef(null);
    const navigate = useNavigate();
    const showToast = useToast()
    const { hasPermission } = usePermissions();

    const handleChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (value.trim().length > 1) {
            setShowDropdown(value.trim().length > 0);
        }
    };

    useEffect(() => {
        if (searchTerm.trim().length < 2) {
            setResults([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        clearTimeout(debounceTimeout.current);

        debounceTimeout.current = setTimeout(() => {
            findPatient(searchTerm)
                .then((data) => setResults(data))
                .finally(() => setLoading(false));
        }, 500);
    }, [searchTerm]);

    const handlePatientClick = (patient) => {
        if (!hasPermission('CONSULTATION_VIEW')) {
            showToast("You don't have permission to view the consultation", "info");
            return
        }

        if (!patient.lastConsultationId) {
            // new user
            // if (patient.isFirstTimeConsultation) {
            showToast("Doctor Consultation not started", "success");
            // }
            // repeat user
            // else if (!patient.isFirstTimeConsultation) {

            // }
        }
        else {
            // if last consultation id is present then move to summary 
            screenBasisPermission(patient.lastConsultationId,patient.id,"view", hasPermission,navigate)
        }
    }

    return (
        <Box sx={{ position: 'relative' }}>
            {/* Blurred view */}
            {showDropdown && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backdropFilter: 'blur(4px)',
                        zIndex: 998,
                    }}
                />
            )}

            {/* Search Input */}
            <TextField
                fullWidth
                inputRef={inputRef}
                placeholder="Search patient..."
                variant="outlined"
                value={searchTerm}
                onChange={handleChange}
                sx={{
                    zIndex: 999,
                    position: 'relative',
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '& fieldset': {
                            border: '1px solid #919191',
                        },
                        '& input': {
                            fontSize: '13px',
                            height: '20px',
                            // backgroundColor: 'rgba(252, 252, 252, 0.5)',
                            '&::placeholder': {
                                fontSize: '12px',       
                                color: 'rgba(84, 84, 84, 1)',         
                                opacity: 1,             
                              },
                        },
                      
                    },
                }}
                size="small"
            />

            {/* Dropdown Results */}
            {showDropdown && (
                <Paper
                    elevation={4}
                    sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        mt: 1,
                        zIndex: 999,
                        maxHeight: 300,
                        overflowY: 'auto',
                        borderRadius: 2,
                        p: 1,
                        width: { xs: '100%', sm: '100%', md: '750px' },
                        maxWidth: { xs: '100%', sm: '100%', md: '90vw' },
                        boxSizing: 'border-box',
                        backgroundColor: '#FFF'
                    }}
                >
                    {loading ? (
                        <Box display="flex" justifyContent="center" py={2}>
                            <CircularProgress size={20} />
                        </Box>
                    ) : results.length > 0 ? (
                        <Grid container>
                            <Grid size={{ md: 12 }} sx={{ textAlign: 'end' }} >
                                <IconButton
                                    onClick={() => {
                                        setSearchTerm('')
                                        setShowDropdown(!showDropdown);
                                    }}
                                    size='small'
                                    sx={{
                                        position: 'absolute',
                                        top: 1,
                                        right: 1,
                                        zIndex: 1000,
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Grid>
                            {results.map((patient, index) => {
                                const riskBg = patient?.riskLevel == "HIGH" ? "#FF9071" : patient?.riskLevel == "MEDIUM" ? "#F0EC8B" : '#D4F0F4';
                                return <Grid
                                    key={patient.id || index}
                                    size={{ xs: 12, sm: 12, md: 12 }}
                                    sx={{
                                        backgroundColor: '#F2F2F2',
                                        borderRadius: 2,
                                        p: { xs: 1.25, md: 1 },
                                        mt: index == 0 ? 3 : 1,
                                        cursor: !patient.lastConsultationId ? 'default' : 'pointer',
                                        minHeight: '47px',
                                        '&:hover': {
                                            backgroundColor: 'rgba(231, 231, 231, 0.7)',
                                        }
                                    }}
                                    onClick={() => patient.lastConsultationId ? handlePatientClick(patient) : null}
                                >
                                    {/* Mobile layout (xs / sm) — single row, type hidden */}
                                    <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ minWidth: 0, flex: 1.4 }}>
                                            <Typography sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 }} fontSize="13px">
                                                {patient.firstName} {patient.lastName}
                                            </Typography>
                                            <Typography sx={{ color: '#666', lineHeight: 1.2 }} fontSize="10px">
                                                {patient.phoneNumber}
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ backgroundColor: riskBg, borderRadius: '4px', minWidth: '50px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 0.75, flexShrink: 0 }} fontSize="10px">
                                            {patient?.riskLevel || '—'}
                                        </Typography>
                                        <Typography sx={{ textAlign: 'center', flexShrink: 0, minWidth: '50px' }} fontSize="11px">
                                            {patient.patientType ==="GYNECOLOGY"? "-": "Week"+" "+ (patient.currentWeek??0)}
                                        </Typography>
                                        <Typography sx={{ cursor: 'pointer', backgroundColor: '#D7F5FF', borderRadius: '4px', minWidth: '64px', textAlign: 'center', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 0.75, boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)', flexShrink: 0 }} fontSize="11px" onClick={(e) => {
                                            e.stopPropagation();
                                            setSearchTerm('')
                                            setShowDropdown(!showDropdown);
                                            handleBookAppointment(patient)
                                        }}>
                                            Book Now
                                        </Typography>
                                    </Box>

                                    {/* Desktop layout (md+) — original */}
                                    <Grid container spacing={1} sx={{ display: { xs: 'none', md: 'flex' } }} alignItems="center">
                                        <Grid size={{ md: 3 }} justifyItems={"flex-start"} >
                                            <Typography sx={{ height: '18px', alignContent: 'center' }} fontSize={"14px"}>
                                                {patient.firstName} {patient.lastName}
                                            </Typography>
                                            <Typography sx={{ height: '18px', alignContent: 'center' }} fontSize={"10px"}>
                                                {patient.phoneNumber}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ md: 2 }} justifyItems={"center"} alignSelf={"center"}>
                                            <Typography sx={{  alignContent: 'center' }} fontSize={"14px"}>
                                                {formatText(patient.patientType)}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ md: 3 }} justifyItems={"center"} alignSelf={"center"}>
                                            <Typography sx={{ backgroundColor: riskBg, borderRadius: '4px', height: '22px', alignContent: 'center', px: 1 }} fontSize={"10px"}>
                                                {patient?.riskLevel}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ md: 2 }} justifyItems={"center"} alignSelf={"center"}>
                                            <Typography sx={{ width: '49px', height: '18px', alignContent: 'center' }} fontSize={"10px"}>
                                              {patient.patientType ==="GYNECOLOGY"? "-": "Week"+" "+ (patient.currentWeek??0)}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ md: 2 }} justifyItems={"center"} alignSelf={"center"}>
                                            {<Typography sx={{ cursor: 'pointer', backgroundColor: '#D7F5FF', borderRadius: '4px', width: '52px', height: '29px', alignContent: 'center', px: 1, boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)' }} fontSize={'10px'} onClick={(e) => {
                                                if (!hasPermission('APPOINTMENT_CREATE')) {
                                                    showToast("You don't have permission to view", "info");
                                                    return
                                                }
                                                e.stopPropagation();
                                                setSearchTerm('')
                                                setShowDropdown(!showDropdown);
                                                handleBookAppointment(patient)
                                            }}>
                                                Book Now
                                            </Typography>}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            })}
                        </Grid>
                    ) : (
                        <Box p={2}>
                             <Grid size={{ md: 12 }} sx={{ textAlign: 'end' }} >
                                <IconButton
                                    onClick={() => {
                                        setSearchTerm('')
                                        setShowDropdown(!showDropdown);
                                    }}
                                    size='small'
                                    sx={{
                                        position: 'absolute',
                                        top: 1,
                                        right: 1,
                                        zIndex: 1000,
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Grid>
                            <Typography variant="body2" color="textSecondary">
                                No results found.
                            </Typography>
                        </Box>
                    )}
                </Paper>
            )}
        </Box>
    );
}
